// Chapter 2 social media campaign — single source of truth for the
// 20-post launch campaign shown on /campaign and exported as PDF.

export const CAMPAIGN_TICKET_LINE =
  "Book tickets — nickfraserartist.com (link in bio)";

const CORE_TAGS =
  "#NickFraser #Chapter2 #OutsiderArt #EastLondon #EastEndVanGogh #LondonArt #ArtExhibition #Shoreditch #HiddenArchive #ArtBrut";

export const CAMPAIGN_WEEKS = [
  { week: 1, dates: "22 — 28 June", title: "The Introduction", note: "Who was Nick Fraser? Establish the man, the myth and the follow." },
  { week: 2, dates: "29 June — 5 July", title: "The Hidden Archive", note: "The discovery story — fifty years of private painting." },
  { week: 3, dates: "6 — 12 July", title: "The Announcement", note: "Chapter 2 is confirmed. Event details, first ticket push." },
  { week: 4, dates: "13 — 19 July", title: "The Story Deepens", note: "Archive, faith, East London roots. Emotional connection." },
  { week: 5, dates: "20 — 26 July", title: "The Offer", note: "Prints, collectors, community and social proof." },
  { week: 6, dates: "27 — 31 July", title: "The Final Push", note: "Urgency. Last tickets. See you at Shoreditch Arts Club." },
];

export const CAMPAIGN_POSTS = [
  // ─── Instagram feed posts ───────────────────────────────────────────
  {
    id: "ig-01",
    no: 1,
    week: 1,
    timing: "Week 1 · Monday 22 June · 6:00pm",
    platform: "Instagram",
    format: "Portrait feed post (4:5)",
    title: "An East End life. A private universe.",
    theme: "Introduction to Nick Fraser",
    image: "/campaign/ig01-intro.png",
    asset: "Archive portrait of Nick (site: /hero/nick-portrait.jpg)",
    visual:
      "Full-bleed charcoal duotone of Nick's archive portrait, heavy grain, dark vignette. Tall condensed headline stacked across the lower third, dates as a hairline eyebrow at the top.",
    graphicText:
      "NICK FRASER · 1946 — 2023  /  AN EAST END LIFE. A PRIVATE UNIVERSE.  /  Chapter 2 — 31 July 2026",
    caption: `Nick Fraser. Born East London, 1946. Died 2023.

To the world, he was a Tate & Lyle factory worker — an East End man shaped by work, faith and family. In private, for more than fifty years, he was building something else entirely: a universe of paintings almost nobody ever saw.

This is Chapter 2. The next part of his story — told in his home city, for one day only, at Shoreditch Arts Club on 31 July 2026.

Follow this page. Over the coming weeks we'll show you who Nick Fraser was — and why he's being called the East End Van Gogh.

Tickets — link in bio.`,
    hashtags: CORE_TAGS + " #ArtistStory #UnseenArt",
    cta: "Follow the journey · Book tickets (link in bio)",
  },
  {
    id: "ig-02",
    no: 4,
    week: 2,
    timing: "Week 2 · Monday 29 June · 6:00pm",
    platform: "Instagram",
    format: "Square feed post (1:1)",
    title: "The artist nobody knew was painting.",
    theme: "The hidden archive story",
    image: "/campaign/ig02-hidden-archive.png",
    asset: "Close crop of 'The Dome' (site: /artworks/the-dome.jpg)",
    visual:
      "Dramatic close crop of a painting filling the top two-thirds; solid ink-black band below with the headline in tall condensed caps and an italic serif subline.",
    graphicText:
      "THE ARTIST NOBODY KNEW WAS PAINTING.  /  A hidden archive, revealed in chapters.  /  NICK FRASER · CHAPTER 2",
    caption: `For fifty years, the paintings stayed inside.

No gallery. No dealer. No audience. Just a man in East London, painting with construction materials and decorating supplies, night after night, because he had to.

When Nick Fraser died in 2023, he left behind a vast archive of work the world had never seen. His lifelong friend Mark O'Shaughnessy and Mark's son Marcus are now bringing it into the light — slowly, deliberately, chapter by chapter.

Chapter 2 opens at Shoreditch Arts Club on 31 July. One day only.

Share this story. Someone you know needs to hear it.`,
    hashtags: CORE_TAGS + " #ArtDiscovery #PosthumousArt",
    cta: "Share the story · Tickets in bio",
  },
  {
    id: "ig-03",
    no: 6,
    week: 2,
    timing: "Week 2 · Saturday 4 July · 11:00am",
    platform: "Instagram",
    format: "Portrait feed post (4:5)",
    title: "From Tate & Lyle to a lifetime of colour.",
    theme: "East End working life and painting",
    image: "/campaign/ig03-tate-lyle.png",
    asset: "'Universal Race' painting (site: /artworks/universal-race.jpg)",
    visual:
      "Split composition — ink-black panel with stacked condensed headline on the left, a tall crop of a vivid painting hung with a thin bone border on the right.",
    graphicText:
      "THE WORKING LIFE  /  FROM TATE & LYLE TO A LIFETIME OF COLOUR.  /  Thirty years on the factory floor. Fifty years of painting in private.",
    caption: `By day: the Tate & Lyle factory. Shift work, graft, the rhythms of industrial East London.

By night: colour. Symbols. Faith. Figures with jewel-tone eyes and open mouths. Paintings made on whatever materials a working man could afford.

Nick Fraser never separated the two lives — he simply never showed anyone the second one.

Chapter 2 brings that second life home to East London. Shoreditch Arts Club, 31 July 2026. One day only.

Tickets — link in bio.`,
    hashtags: CORE_TAGS + " #WorkingClassArt #TateAndLyle #EastEndHistory",
    cta: "Book tickets (link in bio)",
  },
  {
    id: "ig-04",
    no: 7,
    week: 3,
    timing: "Week 3 · Monday 6 July · 6:00pm",
    platform: "Instagram",
    format: "Portrait feed post (4:5) — pin to grid",
    title: "Chapter 2 begins.",
    theme: "Exhibition announcement",
    image: "/campaign/ig04-announcement.png",
    asset: "Projection photograph (site: /hero/projection.jpg)",
    visual:
      "Full-bleed image of the Chapter 2 projection on brick, cinematic black gradient. Enormous condensed headline centred in the lower third with event details in italic serif beneath.",
    graphicText:
      "NICK FRASER — THE EAST END VAN GOGH  /  CHAPTER 2 BEGINS.  /  One day only · Shoreditch Arts Club · 31 July 2026  /  BOOK TICKETS — NICKFRASERARTIST.COM",
    caption: `It's official.

NICK FRASER: CHAPTER 2
Friday 31 July 2026 · Shoreditch Arts Club, London
One day only.

The exhibition — rarely seen paintings from the archive.
The projections — an immersive installation of Nick's universe.
The film — a cinema screening of his story.
The evening — private view with live performance from BoThai.

Day viewings from 12pm (£10) · Evening private view 7pm–10:30pm (£15).

This is the only day. Book now — link in bio — and tag someone who has to be there.`,
    hashtags: CORE_TAGS + " #ShoreditchArtsClub #LondonExhibition #OneDayOnly",
    cta: "Book tickets now (link in bio) · Tag a friend",
  },
  {
    id: "ig-05",
    no: 10,
    week: 3,
    timing: "Week 3 · Sunday 12 July · 5:00pm",
    platform: "Instagram",
    format: "Square feed post (1:1)",
    title: "One day only at Shoreditch Arts Club.",
    theme: "Event-focused post",
    image: "/campaign/ig05-one-day.png",
    asset: "Typographic — strip of 'Through the Blue' (site: /artworks/through-the-blue.jpg)",
    visual:
      "Near-black typographic poster. The date fills the width in giant condensed caps; a narrow film-strip of painting runs along the bottom edge.",
    graphicText:
      "ONE DAY ONLY  /  31 JULY 2026  /  Shoreditch Arts Club, London — 12pm day viewings · 7pm evening private view",
    caption: `Some exhibitions run for months. This one lasts a day.

31 July 2026. Shoreditch Arts Club.

Day viewings: 12pm, 1pm, 2pm — timed entry, £10.
Evening private view: 7pm–10:30pm — projections, film, bar, live performance from BoThai, £15.

When the doors close, the paintings go back to the archive.

Don't watch it happen on someone else's story. Link in bio.`,
    hashtags: CORE_TAGS + " #OneDayOnly #LondonEvents #ThingsToDoInLondon",
    cta: "Book your time slot (link in bio)",
  },
  {
    id: "ig-06",
    no: 12,
    week: 4,
    timing: "Week 4 · Wednesday 15 July · 6:00pm",
    platform: "Instagram",
    format: "Square feed post (1:1)",
    title: "The paintings are split seconds of time.",
    theme: "Quote-led post (campaign line)",
    image: "/campaign/ig06-split-seconds.png",
    asset: "'Light Language' painting (site: /artworks/light-language.jpg)",
    visual:
      "Painting as a darkened full-bleed background; large italic serif quote centred with hairline rules and a small-caps attribution marking it clearly as a campaign line.",
    graphicText:
      "\u201CThe paintings are split seconds of time.\u201D  /  CAMPAIGN LINE · NICK FRASER — CHAPTER 2",
    caption: `"The paintings are split seconds of time."*

Look closely at a Nick Fraser painting and you see it: a moment caught mid-motion. A mouth open in a laugh or a cry. Eyes fixed on something beyond the frame. Loops and dots that feel like a thought happening right now, fifty years ago.

Each canvas is an instant from a private universe — frozen, preserved, and finally visible.

See them in person. Chapter 2 · Shoreditch Arts Club · 31 July. Link in bio.

*Campaign line, written in the spirit of Nick's work — not a direct quotation.`,
    hashtags: CORE_TAGS + " #VisionaryArt #ArtQuotes",
    cta: "See the work in person — tickets in bio",
  },
  {
    id: "ig-07",
    no: 14,
    week: 4,
    timing: "Week 4 · Sunday 19 July · 5:00pm",
    platform: "Instagram",
    format: "Portrait feed post (4:5)",
    title: "A private world, finally seen.",
    theme: "Emotional legacy post",
    image: "/campaign/ig07-private-world.png",
    asset: "'Sign From Above' painting (site: /artworks/sign-from-above.jpg)",
    visual:
      "The painting presented like an altarpiece with a thin bone border on grainy black; reverent condensed headline and quiet italic subline beneath.",
    graphicText:
      "A PRIVATE WORLD, FINALLY SEEN.  /  Painted in silence for five decades. Shown for one day.  /  NICK FRASER · CHAPTER 2 · 31 JULY 2026",
    caption: `He never asked to be seen.

Nick Fraser painted through faith, through hardship, through isolation — not for galleries or praise, but because painting was how he made sense of being alive.

That's what makes standing in front of the work so overwhelming. You're not just looking at paintings. You're being let into a world that was never meant to have visitors.

For one day, the doors are open.

Chapter 2 · Shoreditch Arts Club · 31 July 2026. Link in bio.`,
    hashtags: CORE_TAGS + " #ArtLegacy #FaithAndArt #PrivateWorld",
    cta: "Be there — book tickets (link in bio)",
  },
  {
    id: "ig-08",
    no: 16,
    week: 5,
    timing: "Week 5 · Wednesday 22 July · 6:00pm",
    platform: "Instagram",
    format: "Portrait feed post (4:5) or carousel with print close-ups",
    title: "Limited edition prints.",
    theme: "Universal Soul / Tribal Goddess print announcement",
    image: "/campaign/ig08-prints.png",
    asset: "'Tribal Goddess' + 'Universal Soul of Stars' (site: /release/tribal.jpg, /release/p101-iv.jpg)",
    visual:
      "The two release paintings floated side by side as framed prints with soft shadows on grainy black; print-house typography beneath.",
    graphicText:
      "THE FIRST EDITIONS  /  LIMITED EDITION PRINTS.  /  Tribal Goddess & Universal Soul of Stars — details announced soon.",
    caption: `For the first time, you'll be able to take a piece of Nick's universe home.

Chapter 2 introduces the first limited edition prints from the archive — including Tribal Goddess and Universal Soul of Stars — previewed in person at Shoreditch Arts Club on 31 July.

Edition sizes and pricing will be announced soon. Ticket holders see them first.

Register your interest at nickfraserartist.com and follow this page for the release announcement.`,
    hashtags: CORE_TAGS + " #LimitedEditionPrints #ArtCollector #PrintRelease",
    cta: "Register interest on the website · Tickets in bio",
  },
  {
    id: "ig-09",
    no: 18,
    week: 5,
    timing: "Week 5 · Sunday 26 July · 5:00pm",
    platform: "Instagram",
    format: "Square feed post (1:1) or carousel of quotes",
    title: "What people have said.",
    theme: "Reviews / testimonial post",
    image: "/campaign/ig09-reviews.png",
    asset: "Chapter 1 installation photo (site: /crypt/crypt-1.jpg) + review quotes from /reviews",
    visual:
      "Darkened Chapter 1 installation photograph; layered italic serif quotes with small-caps attributions, museum-caption styling.",
    graphicText:
      "\u201CWorld class.\u201D  /  \u201CSpiritual, tribal, ethereal — an other-worldly experience.\u201D  /  VISITOR REVIEWS · CHAPTER 1, THE CRYPT GALLERY",
    caption: `Don't take our word for it.

"One of the most full-bodied, tender, and devoted installations I have attended in the past year... an artist to remember, and a curator to watch." — Strand Magazine, on Chapter 1

"Spiritual, tribal, ethereal, a faintly ominous other-worldly experience... World class." — Visitor, The Crypt Gallery

"Unforgettable, first shown again here after 30 years." — Strand Magazine

Chapter 1 moved everyone who saw it. Chapter 2 goes further.

Shoreditch Arts Club · 31 July · one day only. Link in bio.`,
    hashtags: CORE_TAGS + " #ArtReview #StrandMagazine #RawVision",
    cta: "Read the reviews on the website · Book tickets",
  },
  {
    id: "ig-10",
    no: 19,
    week: 6,
    timing: "Week 6 · Wednesday 29 July · 6:00pm",
    platform: "Instagram",
    format: "Portrait feed post (4:5) + repost to story",
    title: "31 July 2026 — don't miss it.",
    theme: "Final ticket push / reminder",
    image: "/campaign/ig10-final-push.png",
    asset: "Close crop of 'Universal Gaze' (site: /artworks/universal-gaze.jpg)",
    visual:
      "Extreme close crop on the painted eyes fading into black; giant stacked condensed date and imperative, italic closing line, final-call ticket CTA.",
    graphicText:
      "31 JULY 2026.  /  DON'T MISS IT.  /  One day. One building. A lifetime of work.  /  FINAL TICKETS — NICKFRASERARTIST.COM",
    caption: `Two days.

That's how long you have to decide whether you'll be in the room — or hear about it afterwards.

NICK FRASER: CHAPTER 2
Friday 31 July · Shoreditch Arts Club
Day viewings from 12pm · Evening private view 7pm–10:30pm

A lifetime of hidden work. An immersive projection installation. A film. A live performance. One day only.

Final tickets — link in bio. See you in Shoreditch.`,
    hashtags: CORE_TAGS + " #LastChance #FinalTickets #ThisWeekend",
    cta: "Final tickets — link in bio",
  },

  // ─── Other platforms ────────────────────────────────────────────────
  {
    id: "sm-01",
    no: 5,
    week: 2,
    timing: "Week 2 · Wednesday 1 July · 7:00pm",
    platform: "TikTok / Instagram Reels",
    format: "Vertical video (9:16) — use graphic as reel cover",
    title: "He painted for 50 years. Almost nobody knew.",
    theme: "Viral-style one-sentence introduction",
    image: "/campaign/sm01-reel-cover.png",
    asset: "Reel cover graphic + Chapter 1 film clips / trailer stills for the video itself",
    visual:
      "Reel cover: charcoal duotone portrait of Nick with the stacked one-line hook. Video: slow push-ins on paintings cut with archive portrait and trailer stills, captioned line by line.",
    graphicText:
      "HE PAINTED FOR 50 YEARS.  /  ALMOST NOBODY KNEW.  /  The story of Nick Fraser — the East End Van Gogh.",
    caption: `He worked in a sugar factory for 30 years. He painted in secret for 50. Now East London is finally seeing what he left behind. #NickFraser

On-screen text suggestion (line by line):
1. "This man worked at the Tate & Lyle factory for 30 years."
2. "What almost nobody knew — he was painting. Constantly. For five decades."
3. "He never sold. Never exhibited. He died in 2023, unknown."
4. "Now his archive is being revealed — chapter by chapter."
5. "Nick Fraser: Chapter 2. Shoreditch Arts Club. 31 July. One day only."`,
    hashtags:
      "#NickFraser #Chapter2 #OutsiderArt #ArtTok #HiddenArtist #EastLondon #LondonArt #StoryTime #ArtHistory #EastEndVanGogh",
    cta: "Follow for the full story · Tickets via link in bio",
  },
  {
    id: "sm-02",
    no: 3,
    week: 1,
    timing: "Week 1 · Friday 26 June · 9:00am",
    platform: "LinkedIn",
    format: "Landscape image post (1.91:1)",
    title: "A hidden archive. A lasting legacy.",
    theme: "Legacy, hidden creativity and cultural heritage",
    image: "/campaign/sm02-linkedin.png",
    asset: "'Six Wise Guys' painting (site: /artworks/six-wise-guys.jpg)",
    visual:
      "Wide crop of the multi-figure painting with a bone border beside an ink-black typographic panel — restrained, editorial, boardroom-friendly.",
    graphicText:
      "CULTURAL HERITAGE  /  A HIDDEN ARCHIVE. A LASTING LEGACY.  /  What happens when a lifetime of art is discovered after a life ends?",
    caption: `What happens when a lifetime of creative work is discovered only after a life ends?

Nick Fraser (1946–2023) spent over thirty years working at the Tate & Lyle factory in East London. Privately, from the 1960s until his final years, he produced a vast body of paintings that was almost never exhibited in his lifetime.

His estate — led by his lifelong friend Mark O'Shaughnessy and Mark's son Marcus — is now stewarding that archive into public view. Chapter 1, at The Crypt Gallery, drew critical acclaim, including coverage in Strand Magazine and Raw Vision.

Chapter 2 opens at Shoreditch Arts Club on 31 July 2026, for one day: exhibition, immersive projections, a film screening and the first limited edition prints.

It raises a question worth sitting with: how much extraordinary work is being made, right now, by people whose job titles say something else entirely?

Details and tickets: nickfraserartist.com`,
    hashtags:
      "#CulturalHeritage #OutsiderArt #ArtWorld #Legacy #EastLondon #NickFraser #Chapter2 #CreativeIndustries",
    cta: "Read the story & book — nickfraserartist.com",
  },
  {
    id: "sm-03",
    no: 8,
    week: 3,
    timing: "Week 3 · Tuesday 7 July · 12:00pm",
    platform: "Facebook",
    format: "Event cover / landscape image post (1.91:1)",
    title: "You are invited — Nick Fraser: Chapter 2.",
    theme: "Event-style invitation post",
    image: "/campaign/sm03-facebook-event.png",
    asset: "Projection photo + 'The Dome' inset (site: /hero/projection.jpg, /artworks/the-dome.jpg)",
    visual:
      "Event-banner layout: projection photograph full-bleed, left-aligned event block with title, programme line, venue/date and ticket URL.",
    graphicText:
      "YOU ARE INVITED  /  NICK FRASER: CHAPTER 2  /  One-day exhibition · immersive projections · film screening · live performance  /  SHOREDITCH ARTS CLUB · FRIDAY 31 JULY 2026",
    caption: `YOU'RE INVITED — one day only.

NICK FRASER: CHAPTER 2
📍 Shoreditch Arts Club, London
🗓 Friday 31 July 2026
🎨 Rarely seen paintings from the archive of the "East End Van Gogh"
🎞 Immersive projections + cinema screening of Nick's story
🎶 Evening private view with live performance from BoThai
🎟 Day viewings from 12pm (£10) · Evening private view 7pm–10:30pm (£15)

Nick Fraser painted in private for fifty years. His archive is now being revealed chapter by chapter — and Chapter 2 brings it home to East London.

Tap "Interested", invite your friends, and book at nickfraserartist.com — tickets are limited by venue capacity.`,
    hashtags:
      "#NickFraser #Chapter2 #Shoreditch #LondonEvents #ArtExhibition #EastLondon #ThingsToDoInLondon",
    cta: "Mark Interested · Invite friends · Book at nickfraserartist.com",
  },
  {
    id: "sm-04",
    no: 2,
    week: 1,
    timing: "Week 1 · Wednesday 24 June · 1:00pm",
    platform: "X / Twitter",
    format: "Thread (6 posts) — landscape header graphic on post 1",
    title: "The East End Van Gogh — a thread.",
    theme: "Introducing Nick Fraser's story",
    image: "/campaign/sm04-x-thread.png",
    asset: "'Through the Blue' painting (site: /artworks/through-the-blue.jpg); attach one painting per tweet",
    visual:
      "Thread header: wide crop of the blue painting with 'THE EAST END VAN GOGH.' in condensed caps and a three-beat italic line: Born 1946. Died 2023. Discovered now.",
    graphicText:
      "A THREAD  /  THE EAST END VAN GOGH.  /  Born 1946. Died 2023. Discovered now.",
    caption: `1/ In 2023, a man died in East London. He'd spent 30 years working at the Tate & Lyle sugar factory. Almost nobody knew he was one of the most compelling outsider artists Britain never saw. A thread. 🧵

2/ Nick Fraser, born 1946. By day: factory shifts, East End life, devotion to his mother. By night: paintings — hundreds of them — made with construction materials and decorating supplies. Cosmic, sacred, comic, raw.

3/ He barely showed the work in his lifetime. Small pop-ups — Lewisham Library, the Cutty Sark. No dealer. No gallery. He painted because he had to.

4/ After his death, his lifelong friend Mark O'Shaughnessy and Mark's son Marcus began revealing the archive in chapters. Chapter 1, at The Crypt Gallery, stunned critics — "an artist to remember" (Strand Magazine). Featured in Raw Vision, issue 121.

5/ Now: CHAPTER 2. Shoreditch Arts Club, 31 July 2026. One day only. Exhibition, immersive projections, a film, live performance — and the first limited edition prints.

6/ Fifty years of private work. One day to see it. Tickets: nickfraserartist.com — RT to share Nick's story. He'd never have asked. That's exactly why we should.`,
    hashtags: "#NickFraser #Chapter2 #OutsiderArt #EastLondon #ArtHistory",
    cta: "Repost the thread · Book at nickfraserartist.com",
  },
  {
    id: "sm-05",
    no: 9,
    week: 3,
    timing: "Week 3 · Thursday 9 July · 6:00pm",
    platform: "Instagram / Facebook / X (cross-post)",
    format: "Square quote post (1:1)",
    title: "\u201CNothing will stop me from painting.\u201D",
    theme: "Quote-led post using Nick's words",
    image: "/campaign/sm05-quote.png",
    asset: "'Reflected Grace' painting (site: /artworks/reflected-grace.jpg)",
    visual:
      "Devotional layout: small bordered crop of the painting like an icon above a large italic serif quote on grainy black, with precise attribution.",
    graphicText:
      "\u201CNothing will stop me from painting.\u201D  /  ATTRIBUTED TO NICK FRASER · CHAPTER 1 FILM  /  CHAPTER 2 · 31 JULY 2026 · SHOREDITCH ARTS CLUB",
    caption: `"Nothing will stop me from painting."
— attributed to Nick Fraser, from the Chapter 1 film (as reported in Strand Magazine)

Not poverty. Not obscurity. Not fifty years without an audience.

There's a reason this line stays with everyone who hears it. Most of us wait for permission. Nick never asked.

Hear the full story — and see what the painting made — at Chapter 2. Shoreditch Arts Club, 31 July. Link in bio.`,
    hashtags: CORE_TAGS + " #ArtistQuotes #Determination",
    cta: "Save this post · Book tickets (link in bio)",
  },
  {
    id: "sm-06",
    no: 11,
    week: 4,
    timing: "Week 4 · Monday 13 July · 6:00pm",
    platform: "Instagram / TikTok (cross-post)",
    format: "Square post (1:1) — works as carousel with archive photos",
    title: "Inside the archive.",
    theme: "Behind the scenes — archive and curation journey",
    image: "/campaign/sm06-bts-archive.png",
    asset: "Curator archive photo (site: /crypt/crypt-marcus.jpg); add estate photos if available",
    visual:
      "Documentary frame of the curator among crated works, moody grade, 'INSIDE THE ARCHIVE.' in condensed caps with an intimate italic subline.",
    graphicText:
      "BEHIND THE SCENES  /  INSIDE THE ARCHIVE.  /  Cataloguing five decades of paintings, one canvas at a time.  /  THE ESTATE OF NICK FRASER · CHAPTER 2",
    caption: `Nobody hands you a manual for this.

When Nick died, he left behind five decades of work — canvases, boards, multi-panel pieces — most of it never photographed, titled or shown.

Curating a chapter means holding each painting up to the light and asking: what was he seeing? Every exhibition the estate stages is built this way — slowly, by hand, by people who loved him.

Chapter 2 is the next room in the archive. Step inside on 31 July at Shoreditch Arts Club.

Follow this page to watch the journey continue.`,
    hashtags: CORE_TAGS + " #BehindTheScenes #ArtCuration #ArtistEstate",
    cta: "Follow the journey · Tickets in bio",
  },
  {
    id: "sm-07",
    no: 13,
    week: 4,
    timing: "Week 4 · Friday 17 July · 5:00pm",
    platform: "Instagram / Facebook (cross-post)",
    format: "Square post (1:1)",
    title: "East London made him. Now it welcomes him home.",
    theme: "East London heritage and Shoreditch",
    image: "/campaign/sm07-east-london.png",
    asset: "Projection-on-brick photo + 'Universal Race' (site: /hero/projection.jpg, /artworks/universal-race.jpg)",
    visual:
      "Split frame: night shot of the Chapter 2 projection on East London brick above an ink panel with the two-line homecoming headline.",
    graphicText:
      "EAST LONDON MADE HIM.  /  NOW IT WELCOMES HIM HOME.  /  From the docks and the factory floor to Shoreditch Arts Club.",
    caption: `This is an East London story, start to finish.

Born here in 1946. Worked here — three decades at Tate & Lyle. Painted here, in private, his whole life. The faith, the humour, the grit, the colour in his work — it's all this city.

So it matters that Chapter 2 isn't happening in Mayfair or Paris. It's happening in Shoreditch, a short ride from the streets that made him.

If you're East London — this one's yours. 31 July, Shoreditch Arts Club. Bring your people.`,
    hashtags:
      CORE_TAGS + " #EastEnd #Hackney #TowerHamlets #LondonHistory #Homecoming",
    cta: "Tag your East London people · Book tickets",
  },
  {
    id: "sm-08",
    no: 15,
    week: 5,
    timing: "Week 5 · Monday 20 July · 10:00am",
    platform: "Instagram / LinkedIn (cross-post)",
    format: "Portrait post (4:5)",
    title: "For collectors of the undiscovered.",
    theme: "Aimed at art collectors and galleries",
    image: "/campaign/sm08-collectors.png",
    asset: "'Psychedelic Serenity' (site: /release/p101-iii.jpg)",
    visual:
      "Single painting hung museum-style in a vast dark private-viewing room; auction-house typography with 'THE UNDISCOVERED.' as the headline.",
    graphicText:
      "FOR COLLECTORS  /  THE UNDISCOVERED.  /  A rarely seen archive · private viewings by appointment · first editions announced soon.  /  THE ESTATE OF NICK FRASER",
    caption: `Every collector has one story about the artist they found early. This is that moment.

Nick Fraser (1946–2023): a five-decade archive of British outsider art, critically received at Chapter 1 (Strand Magazine; Raw Vision, issue 121), stewarded by the artist's estate and released deliberately, chapter by chapter.

Chapter 2 — Shoreditch Arts Club, 31 July 2026 — includes a preview of the first limited edition prints, with private collector enquiries welcomed through the website.

For acquisitions, private viewings and estate contact: nickfraserartist.com

Serious interest is invited ahead of the exhibition — the room will be busy.`,
    hashtags:
      "#ArtCollector #OutsiderArt #ArtBrut #RawVision #CollectingArt #NickFraser #Chapter2 #BritishArt",
    cta: "Enquire via nickfraserartist.com · Attend 31 July",
  },
  {
    id: "sm-09",
    no: 17,
    week: 5,
    timing: "Week 5 · Friday 24 July · 5:00pm",
    platform: "Facebook / Instagram (cross-post)",
    format: "Square post (1:1)",
    title: "Be part of Nick's story.",
    theme: "Friends, supporters and community groups",
    image: "/campaign/sm09-community.png",
    asset: "Mark & Marcus photo (site: /curators/mark-marcus.jpg)",
    visual:
      "Warm charcoal duotone of the father-and-son custodians; human, direct headline in condensed caps with a welcoming italic line.",
    graphicText:
      "FRIENDS, FAMILY & COMMUNITY  /  BE PART OF NICK'S STORY.  /  Join us for one night as East London remembers one of its own.  /  CHAPTER 2 · SHOREDITCH ARTS CLUB · 31 JULY 2026",
    caption: `This exhibition wasn't built by an institution. It was built by people who loved him.

Mark O'Shaughnessy — Nick's lifelong friend and muse — and his son Marcus have carried the archive this far. Chapter 1 happened because friends, neighbours and strangers showed up, shared posts and bought tickets.

Chapter 2 needs the same again.

If you knew Nick, knew the East End he came from, or simply believe stories like his deserve to be told — come. Bring someone. Share this post.

31 July · Shoreditch Arts Club · day viewings from 12pm, evening private view from 7pm. Tickets at nickfraserartist.com.`,
    hashtags: CORE_TAGS + " #CommunitySpirit #SupportLocalArt #EastLondonCommunity",
    cta: "Share this post · Bring someone on 31 July",
  },
  {
    id: "sm-10",
    no: 20,
    week: 6,
    timing: "Week 6 · Thursday 30 July · 9:00am + 6:00pm (repost)",
    platform: "Instagram Stories / Facebook Stories / TikTok",
    format: "Story (9:16) with link sticker",
    title: "Last chance to book.",
    theme: "Final 'last chance' post",
    image: "/campaign/sm10-last-chance.png",
    asset: "'The Dome' darkened crop (site: /artworks/the-dome.jpg)",
    visual:
      "Full-bleed golden painting darkened to near-black; 'LAST CHANCE TO BOOK.' in giant stacked condensed caps with a button-style BOOK NOW label for the link sticker.",
    graphicText:
      "TOMORROW · ONE DAY ONLY  /  LAST CHANCE TO BOOK.  /  Nick Fraser: Chapter 2 — Shoreditch Arts Club, 31 July.  /  BOOK NOW — LINK IN BIO",
    caption: `TOMORROW. Then it's gone.

Nick Fraser: Chapter 2 — one day only at Shoreditch Arts Club.

Fifty years of hidden paintings. Immersive projections. The film. Live performance from BoThai at the evening private view.

Last tickets 👉 tap the link sticker.

(Story tip: post at 9am, repost at 6pm with a countdown sticker set to 12pm on 31 July.)`,
    hashtags: "#NickFraser #Chapter2 #LastChance #London #OneDayOnly",
    cta: "Tap the link sticker — book now",
  },
];

export const CAMPAIGN_SCHEDULE = [...CAMPAIGN_POSTS].sort((a, b) => a.no - b.no);
